import { useCallback, useState } from 'react';

import { Button, Checkbox, ModalBase, ModalBody, ModalFooter, ModalHeader, Stack, u } from '@ui';

export const VIEW_PRESETS = {
    FACE_ON: 'face_on',
    DOWN_THE_LINE: 'down_the_line',
    TOP: 'top',
    BACKSIDE: 'backside',
} as const;

export type ViewPreset = (typeof VIEW_PRESETS)[keyof typeof VIEW_PRESETS];

type PresetOption = {
    id: ViewPreset;
    label: string;
    description: string;
};

const PRESET_OPTIONS: PresetOption[] = [
    {
        id: VIEW_PRESETS.FACE_ON,
        label: 'Face On',
        description: 'Along the X axis, looking at the pelvis',
    },
    {
        id: VIEW_PRESETS.DOWN_THE_LINE,
        label: 'Down the Line',
        description: 'Looking along the shaft plane at P1',
    },
    {
        id: VIEW_PRESETS.TOP,
        label: 'Top',
        description: 'Along the Z axis, looking at the hands',
    },
    {
        id: VIEW_PRESETS.BACKSIDE,
        label: 'Backside',
        description: 'Diagonal view perpendicular to the spine',
    },
];

type ViewPresetsModalProps = {
    open: boolean;
    onDismiss: () => void;
    onApply: (preset: ViewPreset) => void;
    currentPreset: ViewPreset | null;
};

export function ViewPresetsModal({ open, onDismiss, onApply, currentPreset }: ViewPresetsModalProps) {
    const [selectedPreset, setSelectedPreset] = useState<ViewPreset | null>(null);

    const displayedPreset = selectedPreset ?? currentPreset;

    const handleApply = useCallback(() => {
        if (displayedPreset) {
            onApply(displayedPreset);
            setSelectedPreset(null);
            onDismiss();
        }
    }, [displayedPreset, onApply, onDismiss]);

    const handleDismiss = useCallback(() => {
        setSelectedPreset(null);
        onDismiss();
    }, [onDismiss]);

    return (
        <ModalBase open={open} onDismiss={handleDismiss} variant="partial">
            <ModalHeader>Camera View</ModalHeader>
            <ModalBody py={u(4)} flexShrink={1}>
                <Stack>
                    {PRESET_OPTIONS.map((preset) => (
                        <Checkbox
                            key={preset.id}
                            label={preset.label}
                            checked={displayedPreset === preset.id}
                            onChange={() => setSelectedPreset(preset.id)}
                            fullWidth
                        />
                    ))}
                </Stack>
            </ModalBody>
            <ModalFooter
                primary={
                    <Button variant="primary" size="medium" onClick={handleApply} isDisabled={!displayedPreset}>
                        Apply
                    </Button>
                }
                secondary={
                    <Button variant="secondary" size="medium" onClick={handleDismiss}>
                        Cancel
                    </Button>
                }
            />
        </ModalBase>
    );
}

export function getPresetLabel(preset: ViewPreset | null): string {
    if (!preset) return 'None';
    const option = PRESET_OPTIONS.find((p) => p.id === preset);
    return option?.label ?? 'None';
}
